import { useEffect, useRef } from 'react'
import { type ColorMode, useColorMode } from '~/components/ui/color-mode'
import { events } from '~/hooks/useElectronIPC'
import { isElectron } from '~/utils/common'

/**
 * Two-way bridge between the Electron "Theme" menu and next-themes.
 *
 * The renderer owns the theme (next-themes / localStorage), so the menu is kept
 * in sync rather than duplicating state: clicking a radio sends 'set-color-mode'
 * (applied here), and any preference change is reported back via
 * 'color-mode-changed' so the menu radio reflects the real value. Reporting on
 * mount corrects the menu after a restart or a change made elsewhere.
 *
 * Renders nothing. No-op outside Electron.
 */
export const ElectronThemeSync = () => {
  const { colorModePreference, setColorMode } = useColorMode()

  // useColorMode returns a fresh setter identity each render, so hold the latest
  // in a ref and subscribe once rather than re-attaching the listener per render.
  const setColorModeRef = useRef(setColorMode)
  setColorModeRef.current = setColorMode

  useEffect(() => {
    if (!isElectron) return

    const handleSetColorMode = (mode: ColorMode) =>
      setColorModeRef.current(mode)
    window.electron?.receive(events.setColorMode, handleSetColorMode)

    // `receive` wraps the callback, so it can only be detached by channel.
    return () => {
      window.electron?.removeAllListeners(events.setColorMode)
    }
  }, [])

  useEffect(() => {
    if (!isElectron) return
    window.electron?.send(events.colorModeChanged, colorModePreference)
  }, [colorModePreference])

  return null
}
